import styles from "./Cart.module.css"
import React, {useEffect, useState, useMemo} from 'react';
import { XCircle } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useCartContext } from "../../context/CartContext";
import { useTimeModal } from "../../context/TimeModalContext";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useOrderContext } from "../../context/OrderContext";
import { usePreferences } from "../../context/PreferencesContext";
import { useTranslation } from "react-i18next";
import { czk_eur_rate } from "../../constants/prices";
import useEnsureCatalogData from "../../hooks/useEnsureCatalogData";
import { getCartBlockPrice, getCartPriceSummary, getCartPriceSummaryCurrency, getGroupedBlockCount, getSvozBlockCount, groupCartData, hasTransportForItems } from "../../utils/cart";
import { normalizeDate } from "../../utils/calendar";
import { deleteCart } from "../../services/cartService";
import { useUserContext } from "../../context/UserContext";
import { Subscriber } from "../../types/auth.types";
import { CartItem } from "../../types/cart.types";
import { log } from "../../utils/logger";
import CartBlockList from "./CartBlockList";
import CartSummary from "./CartSummary";
import CartSummaryActions from "./CartSummaryActions";



/**
* Cart
* Contains all the products in order and order summary.
* Handles changing products delivery time and redirecting to checkout
*
* @returns TSX.Element
*/
const Cart = () => { 
    const { t } = useTranslation(); // Translation
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const screenWidth = useWindowSize(); // Window width
    const { cartOpen, closeCart, cartData, reloadCart} = useCartContext(); // Cart context
    const { open, setTimeTarget, setOpenOnTop } = useTimeModal(); // Time modal toggle 
    const {userData} = useUserContext(); // User context
    const [user, setUser] = useState<Subscriber>()    
    useEffect(() => {
        const u = userData?.subscribers?.find(sub => sub.subscriber_id === orderData.recipient)
        setUser(u)
    }, [userData])
    const { allProducts, packages, items, activities } = useEnsureCatalogData({
        productSource: "allProducts",
    });
    
    const navigate = useNavigate(); // Navigate
    const { orderData, orderType, resetOrderData } = useOrderContext(); // Order data
    
    const groupedCart = groupCartData(cartData ?? null) // Grouped cart data by date and time
    const blockLookup = useMemo(
        () => ({
            sales: allProducts,
            packages,
            storage: items,
        }),
        [allProducts, packages, items]
    );

    // Handle going to checkout
    const onCheckout = () => {
        closeCart();
        navigate("/checkout");
    }

    // Cart drop
    const handleDropCart = () => {
        deleteCart()
            .then((data) => {
            //log(data)
            reloadCart();
        })
        .then(()=>{
            closeCart();
            resetOrderData();
            //log("Cart dropped");
        })
    }
    // Check if all blocks have correct value
    const allBlocksEnough = useMemo(() => {
        const minCZK = user?.minimal_order_value ?? 5000;
        if (!groupedCart || !Object.keys(groupedCart).length) return false;

        // groupedCart: Record<date, Record<time, CartItem[]>>
        return Object.values(groupedCart).every((timesObj) =>
            Object.values(timesObj).every((products: CartItem[]) => {
            const priceCZK = getCartBlockPrice(products, blockLookup);
            return priceCZK >= minCZK;
            })
        );
    }, [blockLookup, groupedCart, user?.minimal_order_value]);


    const groupedBlocksCount = getGroupedBlockCount(groupedCart);
    // Handle transport - svoz
    const [transportEx, setTransportEx] = useState<boolean>(false);
    useEffect(() => {
        if (!cartData?.items) return;
        const hasTransport = hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
        setTransportEx(hasTransport);
    }, [cartData?.items, items, allProducts, packages]);

    const priceSummary = useMemo(
        () => getCartPriceSummary(cartData, groupedBlocksCount, getSvozBlockCount(groupedCart, { sales: allProducts, packages, storage: items })),
        [cartData, groupedBlocksCount, groupedCart, allProducts, packages, items]
    );
    const priceSummaryCurrency = useMemo(
        () => getCartPriceSummaryCurrency(priceSummary, selectedCurrency, czk_eur_rate),
        [priceSummary, selectedCurrency]
    );

    return(
        <>
            <div className={styles.CartWrapper} style={cartOpen == false ? { display: "none" } : undefined}>
                <div className={styles.CartBlur} onClick={closeCart}></div>
                <div className={styles.Cart} >
                    <button className={styles.CartClose} onClick={()=>closeCart()}>
                        <XCircle/>
                    </button>
                    <div className={styles.CartHeader}>
                        <p className={styles.CartTitle}>{orderType === "demand" ? t("cart.demand_title") : t("cart.title")}</p>
                    </div>
                    <div className={styles.CartBody}>
                        <div className={styles.CartContent}>
                            <CartBlockList
                                groupedCart={groupedCart}
                                cartData={cartData}
                                orderType={orderType}
                                user={user}
                                selectedCurrency={selectedCurrency}
                                blockLookup={blockLookup}
                                allProducts={allProducts}
                                packages={packages}
                                items={items}
                                activities={activities}
                                transport={priceSummaryCurrency.deliveryNoTax}
                                isDesktop={screenWidth > 900}
                                onEditTime={(date, time) => {
                                    setOpenOnTop(true);
                                    setTimeTarget({
                                        type: "group",
                                        date: normalizeDate(date),
                                        time: time,
                                    });
                                    open();
                                }}
                            />
                        </div>
                        <CartSummary
                            isDesktop={screenWidth > 900}
                            groupedCart={groupedCart}
                            cartData={cartData}
                            orderType={orderType}
                            selectedCurrency={selectedCurrency}
                            blockLookup={blockLookup}
                            transportEx={transportEx}
                            priceSummaryCurrency={priceSummaryCurrency}
                            onDropCart={handleDropCart}
                            onCheckout={onCheckout}
                            allBlocksEnough={allBlocksEnough}
                        />
                        
                    </div>
                    {screenWidth > 900 && (
                        <CartSummaryActions
                            className={styles.CheckoutButtons}
                            onDropCart={handleDropCart}
                            onCheckout={onCheckout}
                            disabled={!allBlocksEnough}
                        />
                    )}
                </div>
            </div>
        </>
    )
}
export default Cart
